import { isRecord, parseApiResponse } from '@/shared/utils/apiResponse';
import { apiGet } from '@/shared/utils/requestBuilder';

import type {
  ConversaoDaNotificacao,
  ErroDaConsultaDaConversao,
  ResultadoDaConversao,
} from '../../types/conversao.types';
import { lerOrigemDoContato } from '../../utils/origemDoContato';

export const MENSAGEM_DE_FALHA_DA_CONVERSAO =
  'Não foi possível consultar a conversão. Tente novamente.';

/** Sem status HTTP que explique a falha: rede caída ou resposta 2xx fora do contrato. */
export const STATUS_FORA_DO_CONTRATO = 0;

/** Rejeição com o status HTTP: o thunk separa recusa do core (4xx) de falha (rede, 5xx, contrato). */
export class FalhaDaConsultaDaConversao extends Error {
  readonly erro: ErroDaConsultaDaConversao;

  constructor(status: number) {
    super(MENSAGEM_DE_FALHA_DA_CONVERSAO);
    this.name = 'FalhaDaConsultaDaConversao';
    this.erro = { status, mensagem: MENSAGEM_DE_FALHA_DA_CONVERSAO };
  }
}

const instanteIso = (valor: unknown): valor is string =>
  typeof valor === 'string' && !Number.isNaN(Date.parse(valor));

const instanteOuNulo = (valor: unknown): valor is string | null =>
  valor === null || instanteIso(valor);

function lerConversao(valor: unknown): ConversaoDaNotificacao | null {
  if (!isRecord(valor)) return null;
  if (valor.estado === 'SEM_CONVERSAO') {
    return valor.data === null && valor.origem === null
      ? { estado: 'SEM_CONVERSAO', data: null, origem: null }
      : null;
  }
  if (valor.estado !== 'REGISTRADA' || !instanteIso(valor.data)) return null;
  if (valor.origem === null) return { estado: 'REGISTRADA', data: valor.data, origem: null };
  const origem = lerOrigemDoContato(valor.origem);
  return origem ? { estado: 'REGISTRADA', data: valor.data, origem } : null;
}

/**
 * Resposta fora do contrato é falha, e não "sem conversão": ler um payload quebrado como ausência
 * diria ao advogado que não houve conversão quando a tela só não conseguiu saber.
 */
function lerResultadoDaConversao(valor: unknown): ResultadoDaConversao {
  const conversao = isRecord(valor) ? lerConversao(valor.conversao) : null;
  const atualizadaEm = isRecord(valor) ? valor.atualizadaEm : undefined;
  if (!conversao || !instanteOuNulo(atualizadaEm)) {
    throw new FalhaDaConsultaDaConversao(STATUS_FORA_DO_CONTRATO);
  }
  return { conversao, atualizadaEm };
}

export async function consultarConversaoDaNotificacao(
  itemId: string,
  signal: AbortSignal,
): Promise<ResultadoDaConversao> {
  const response = await apiGet(
    `/extrajudicial/notificacoes/itens/${encodeURIComponent(itemId)}/conversao`,
    undefined,
    false,
    signal,
  );
  if (!response.ok) throw new FalhaDaConsultaDaConversao(response.status);
  const dados = await parseApiResponse<unknown>(response).catch(() => {
    throw new FalhaDaConsultaDaConversao(STATUS_FORA_DO_CONTRATO);
  });
  return lerResultadoDaConversao(dados);
}
